import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Activity, AlertTriangle, CheckCircle2, Loader2, PauseCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// idefix Glowy Studio — "Fırsat Yağmuru" toplu kare üretimi izleme (yalnız Efe).
// Veri: glowytics-backend/scripts/idefix_studio/firsat/uret.py → durum/uretim.json
//   → GET /api/idefix-studio/uretim (2 sn'de bir). Sayfa üretimi başlatmaz/durdurmaz.

const API = 'https://api.glowytics.com/api/idefix-studio/uretim';
const IZINLI = ['efe@heyglowy.com'];
const MAVI = '#2563eb';   // bizim iş
const GRI = '#94a3b8';    // makinedeki diğer işler
const ESIK = '#dc2626';

const sayi = (n, h = 0) => (n == null ? '—' : Number(n).toLocaleString('tr-TR', { maximumFractionDigits: h, minimumFractionDigits: h }));
const sure = (sn) => {
  if (sn == null || !isFinite(sn)) return '—';
  sn = Math.round(sn);
  const s = Math.floor(sn / 3600), d = Math.floor((sn % 3600) / 60), n = sn % 60;
  return s ? `${s} sa ${d} dk` : d ? `${d} dk ${n} sn` : `${n} sn`;
};
const saat = (t) => new Date(t * 1000).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

function Kutucuk({ etiket, deger, alt, vurgu }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="text-xs font-medium text-gray-500">{etiket}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${vurgu || 'text-gray-900'}`}>{deger}</div>
      {alt && <div className="mt-0.5 text-xs text-gray-500">{alt}</div>}
    </div>
  );
}

/** Tek eksenli zaman çizgisi; seriler: [{ad, renk, al: o => sayı}], esik: {deger, ad}. */
function Cizgi({ baslik, birim, seri, cizgiler, esik, yMin = 0, yMaxEn = 1 }) {
  const ref = useRef(null);
  const [imlec, setImlec] = useState(null);
  const W = 560, H = 170, P = { l: 40, r: 12, t: 10, b: 22 };
  const noktalar = seri.filter((o) => cizgiler.every((c) => c.al(o) != null));
  if (noktalar.length < 2) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="text-sm font-medium text-gray-800">{baslik}</div>
        <div className="flex h-[170px] items-center justify-center text-xs text-gray-400">Veri bekleniyor…</div>
      </div>
    );
  }
  const t0 = noktalar[0].t, t1 = noktalar[noktalar.length - 1].t;
  const yMax = Math.max(yMaxEn, esik ? esik.deger * 1.1 : 0, ...noktalar.flatMap((o) => cizgiler.map((c) => c.al(o)))) * 1.08;
  const x = (t) => P.l + ((t - t0) / Math.max(1, t1 - t0)) * (W - P.l - P.r);
  const y = (v) => H - P.b - ((v - yMin) / (yMax - yMin)) * (H - P.t - P.b);
  const yol = (c) => noktalar.map((o, i) => `${i ? 'L' : 'M'}${x(o.t).toFixed(1)},${y(c.al(o)).toFixed(1)}`).join('');
  const izgara = [0, 0.5, 1].map((f) => yMin + f * (yMax - yMin));
  const hover = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    let en = noktalar[0];
    for (const o of noktalar) if (Math.abs(x(o.t) - px) < Math.abs(x(en.t) - px)) en = o;
    setImlec(en);
  };
  const son = noktalar[noktalar.length - 1];
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-sm font-medium text-gray-800">{baslik}</div>
        {cizgiler.length > 1 && (
          <div className="flex gap-3 text-xs text-gray-600">
            {cizgiler.map((c) => (
              <span key={c.ad} className="flex items-center gap-1">
                <span className="inline-block h-0.5 w-3 rounded" style={{ background: c.renk }} />{c.ad}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="relative">
        <svg ref={ref} viewBox={`0 0 ${W} ${H}`} className="mt-2 w-full" onMouseMove={hover} onMouseLeave={() => setImlec(null)}>
          {izgara.map((v) => (
            <g key={v}>
              <line x1={P.l} x2={W - P.r} y1={y(v)} y2={y(v)} stroke="#eef0f3" />
              <text x={P.l - 6} y={y(v) + 3} textAnchor="end" fontSize="10" fill="#6b7280">{sayi(v, v < 10 && v % 1 ? 1 : 0)}</text>
            </g>
          ))}
          {esik && esik.deger <= yMax && (
            <g>
              <line x1={P.l} x2={W - P.r} y1={y(esik.deger)} y2={y(esik.deger)} stroke={ESIK} strokeDasharray="4 4" strokeWidth="1" />
              <text x={W - P.r} y={y(esik.deger) - 4} textAnchor="end" fontSize="10" fill="#6b7280">{esik.ad}</text>
            </g>
          )}
          {cizgiler.map((c) => <path key={c.ad} d={yol(c)} fill="none" stroke={c.renk} strokeWidth="2" strokeLinejoin="round" />)}
          {cizgiler.map((c) => (
            <text key={c.ad + 's'} x={x(son.t) - 2} y={y(c.al(son)) - 6} textAnchor="end" fontSize="10" fill="#374151" fontWeight="600">
              {sayi(c.al(son), 1)}{birim}
            </text>
          ))}
          <text x={P.l} y={H - 6} fontSize="10" fill="#6b7280">{saat(t0)}</text>
          <text x={W - P.r} y={H - 6} fontSize="10" fill="#6b7280" textAnchor="end">{saat(t1)}</text>
          {imlec && (
            <g>
              <line x1={x(imlec.t)} x2={x(imlec.t)} y1={P.t} y2={H - P.b} stroke="#9ca3af" strokeWidth="1" />
              {cizgiler.map((c) => <circle key={c.ad} cx={x(imlec.t)} cy={y(c.al(imlec))} r="4" fill={c.renk} stroke="#fff" strokeWidth="2" />)}
            </g>
          )}
        </svg>
        {imlec && (
          <div className="pointer-events-none absolute top-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs shadow-sm"
            style={{ left: `${Math.min(70, (x(imlec.t) / W) * 100)}%` }}>
            <div className="text-gray-500">{saat(imlec.t)}</div>
            {cizgiler.map((c) => (
              <div key={c.ad} className="tabular-nums text-gray-800">{c.ad}: <b>{sayi(c.al(imlec), 1)}{birim}</b></div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function IdefixUretim() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [hata, setHata] = useState(null);
  const izinli = IZINLI.includes(String(user?.email || '').toLowerCase());

  useEffect(() => {
    if (!izinli) return undefined;
    let dur = false;
    const cek = async () => {
      try {
        const r = await fetch(API, { credentials: 'include' });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || r.status);
        if (!dur) { setD(j); setHata(null); }
      } catch (e) { if (!dur) setHata(e.message); }
    };
    cek();
    const id = setInterval(cek, 2000);
    return () => { dur = true; clearInterval(id); };
  }, [izinli]);

  const seri = useMemo(() => {
    const s = d?.seri || [];
    // ürün/sn: iki ölçüm arası biten farkı
    return s.map((o, i) => ({ ...o, hiz_an: i ? Math.max(0, (o.biten - s[i - 1].biten) / Math.max(0.5, o.t - s[i - 1].t)) : null }));
  }, [d]);

  if (!izinli) return <div className="p-8 text-sm text-gray-500">Bu sayfa iç kullanıma açık.</div>;
  if (!d) return <div className="flex items-center gap-2 p-8 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" />{hata || 'Yükleniyor…'}</div>;
  if (d.durum === 'yok') return <div className="p-8 text-sm text-gray-500">Henüz bir üretim koşusu yok.</div>;

  const o = d.son_olcum || {};
  const bitti = d.durum === 'bitti';
  const canli = !bitti && d.bayat_sn < 15;
  const yuzde = d.hedef ? (100 * (d.biten + d.hata)) / d.hedef : 0;
  const kalan = d.hiz > 0 ? (d.hedef - d.biten - d.hata) / d.hiz : null;
  const katalogSure = d.hiz > 0 ? d.katalog / d.hiz : null;
  const kbUrun = d.biten ? d.kb_toplam / d.biten : null;
  const gecen = bitti ? (d.bitis - d.uretim_baslangic) : d.gecen_sn;

  let rozet;
  if (bitti) rozet = <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />Bitti</span>;
  else if (!canli) rozet = <span className="flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700"><AlertTriangle className="h-3.5 w-3.5" />{d.bayat_sn} sn'dir veri yok — süreç durmuş olabilir</span>;
  else if (d.fren) rozet = <span className="flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700"><PauseCircle className="h-3.5 w-3.5" />Fren: makine yüklü, bekliyor</span>;
  else rozet = <span className="flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700"><Activity className="h-3.5 w-3.5" />{d.durum === 'uretiliyor' ? 'Üretiliyor' : d.durum}</span>;

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">idefix · Fırsat Yağmuru üretimi</h1>
          <p className="text-sm text-gray-500">
            Koşu {d.kosu_id} · {d.surec} süreç × {d.is_parcacigi} iş parçacığı · nice 19 · fren: yük &gt; {d.esik?.yuk1} ya da backend &gt; {sayi(d.esik?.backend_ms)} ms
          </p>
        </div>
        {rozet}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-baseline justify-between text-sm">
          <span className="font-medium text-gray-800">{sayi(d.biten)} / {sayi(d.hedef)} ürün</span>
          <span className="tabular-nums text-gray-500">%{sayi(yuzde, 1)}</span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-100">
          <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${Math.min(100, yuzde)}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kutucuk etiket="Hız" deger={`${sayi(d.hiz, 1)} ürün/sn`} alt={`ürün başına 2 kare · ${sayi((d.hiz || 0) * 60)} ürün/dk`} />
        <Kutucuk etiket={bitti ? 'Toplam süre' : 'Geçen / kalan'} deger={bitti ? sure(gecen) : sure(gecen)} alt={bitti ? `feed okuma ${sure(d.feed_sn)}` : `kalan ≈ ${sure(kalan)}`} />
        <Kutucuk etiket="Tüm katalog tahmini" deger={sure(katalogSure)} alt={`${sayi(d.katalog)} ürün, bu hızla`} vurgu="text-blue-700" />
        <Kutucuk etiket="Hata" deger={sayi(d.hata)} alt={d.biten ? `%${sayi((100 * d.hata) / (d.biten + d.hata), 2)}` : null} vurgu={d.hata ? 'text-rose-700' : 'text-gray-900'} />
        <Kutucuk etiket="B2'ye yazılan" deger={`${sayi(d.kb_toplam / 1048576, 2)} GB`} alt={kbUrun ? `ürün başına ${sayi(kbUrun)} KB → katalog ≈ ${sayi((kbUrun * d.katalog) / 1048576)} GB` : null} />
        <Kutucuk etiket="İndirilen görsel" deger={`${sayi(d.indir_kb_toplam / 1048576, 2)} GB`} alt={d.biten ? `katalog ≈ ${sayi((d.indir_kb_toplam / d.biten) * d.katalog / 1048576)} GB` : null} />
        <Kutucuk etiket="Ürün başına süre" deger={`${sayi((d.ort_ms?.indir || 0) + (d.ort_ms?.ciz || 0) + (d.ort_ms?.yukle || 0))} ms`}
          alt={`indir ${sayi(d.ort_ms?.indir)} · çiz ${sayi(d.ort_ms?.ciz)} · yükle ${sayi(d.ort_ms?.yukle)}`} />
        <Kutucuk etiket="Fren süresi" deger={sure(d.fren_sn)} alt="makine yüklüyken beklenen" vurgu={d.fren_sn ? 'text-amber-700' : 'text-gray-900'} />
      </div>

      <h2 className="pt-2 text-sm font-semibold text-gray-700">Makine (48 çekirdek) — anlık: yük {sayi(o.yuk1, 2)} · CPU %{sayi(o.cpu, 1)} · boş RAM {sayi(o.ram_bos_gb, 1)} GB · boş disk {sayi(o.disk_bos_gb)} GB</h2>
      <div className="grid gap-3 md:grid-cols-2">
        <Cizgi baslik="CPU kullanımı (tüm çekirdeklerin %'si)" birim="%" seri={seri} yMaxEn={10}
          cizgiler={[{ ad: 'Bu iş', renk: MAVI, al: (x) => x.cpu_biz }, { ad: 'Diğer işler', renk: GRI, al: (x) => x.cpu_diger }]} />
        <Cizgi baslik="Sistem yükü (load 1 dk)" birim="" seri={seri} yMaxEn={5} esik={{ deger: d.esik?.yuk1, ad: 'fren eşiği' }}
          cizgiler={[{ ad: 'Yük', renk: MAVI, al: (x) => x.yuk1 }]} />
        <Cizgi baslik="Backend yanıt süresi (diğer işler etkileniyor mu?)" birim=" ms" seri={seri} yMaxEn={50}
          cizgiler={[{ ad: 'Backend', renk: MAVI, al: (x) => x.backend_ms }]} />
        <Cizgi baslik="Ağ trafiği" birim=" Mbps" seri={seri} yMaxEn={10}
          cizgiler={[{ ad: 'İndirme', renk: MAVI, al: (x) => x.ag_in_mbps }, { ad: 'Yükleme', renk: GRI, al: (x) => x.ag_out_mbps }]} />
        <Cizgi baslik="Üretim hızı (ürün/sn)" birim="" seri={seri} yMaxEn={1}
          cizgiler={[{ ad: 'Hız', renk: MAVI, al: (x) => x.hiz_an }]} />
        <Cizgi baslik="Boş RAM" birim=" GB" seri={seri} yMaxEn={10}
          cizgiler={[{ ad: 'Boş RAM', renk: MAVI, al: (x) => x.ram_bos_gb }]} />
      </div>

      {d.son_kareler?.length > 0 && (
        <>
          <h2 className="pt-2 text-sm font-semibold text-gray-700">Son üretilen kareler</h2>
          <div className="grid grid-cols-3 gap-3 md:grid-cols-6">
            {d.son_kareler.map((k) => (
              <a key={k.id} href={k.k45} target="_blank" rel="noreferrer" className="group block overflow-hidden rounded-lg border border-gray-200 bg-white">
                <img src={k.k11} alt={k.id} loading="lazy" className="aspect-square w-full object-cover" />
                <div className="truncate px-2 py-1 text-[11px] text-gray-500">{k.id}</div>
              </a>
            ))}
          </div>
        </>
      )}

      {d.son_hatalar?.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-4">
          <div className="text-sm font-medium text-rose-800">Son hatalar</div>
          <ul className="mt-2 space-y-1 text-xs text-rose-900">
            {d.son_hatalar.map((h, i) => <li key={i} className="break-all">{h.id} — {h.hata}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
